/* eslint-disable consistent-return */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { GrMicrophone } from 'react-icons/gr';
import { FiVideo, FiBookOpen } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

// import showNotification from 'utils/showNotification';
import fakeImg from 'assets/images/fake-img-chat.png';
import { formatMessage, stripHtml } from 'utils/string';
import { handleTrackEvent } from 'utils/analytics';
import { CHAT_ROLES } from '../utils';
import { useEvieAppContext } from '../EvieAppProvider';
import { useEvieChatContext } from '../EvieChatProvider';

import AssistantAvatar from '../AssistantAvatar';
import ScheduleCallCard from '../ScheduleCallCard';
import ChatProgramCard from '../ChatProgramCard';
// import UserFeedback from '../UserFeedback';

/* function onCopyToClipboard(text) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showNotification({
          messages: ['Text copied to clipboard successfully!'],
        });
      })
      .catch((err) => {
        showNotification({
          messages: ['Could not copy text. Please try again.'],
        });
        console.error('Could not copy text: ', err);
      });
  }
 */

const RECOMMENDATION_ASSETS = {
  exercise: {
    linkLabel: 'evieApp.assistant.recommendations.exercise',
    icon: <FiVideo size={20} />,
  },
  audio: {
    linkLabel: 'evieApp.assistant.recommendations.audio',
    icon: <GrMicrophone size={20} />,
  },
  short_video: {
    linkLabel: 'evieApp.assistant.recommendations.shortVideo',
    icon: <FiVideo size={20} />,
  },
  reading: {
    linkLabel: 'evieApp.assistant.recommendations.reading',
    icon: <FiBookOpen size={20} />,
  },
  squeeze: {
    linkLabel: 'evieApp.assistant.recommendations.squeeze',
    icon: <GrMicrophone size={20} />,
  },
};

const MsgBubble = ({ message, isNew, onAnimationComplete }) => {
  const { t } = useTranslation();
  const { isAppointmentActive } = useEvieAppContext();
  const { selectContent, changeAssistantStatus } = useEvieChatContext();
  const { role, createdAt, content, contentRecommendations = [], chatAction = null } = message;
  const { type, trigger, key: eventKey, payload = null } = chatAction || {};
  const { event_type_id: eventId } = payload || {};
  const messageHour = createdAt.slice(11, 16);

  const [displayedContent, setDisplayedContent] = useState(content);
  const animationDone = !isNew || displayedContent === content;
  const isAssistant = role === CHAT_ROLES.ASSISTANT;
  const isPayload = chatAction && chatAction.payload !== null;
  const isCallFields = chatAction && chatAction.type === 'call_offer' && chatAction.key === 'discovery_call';
  const isProgramFields = chatAction && chatAction.type === 'program_shortcut' && chatAction.key === 'today_session';
  const isScheduleCallCard = isAppointmentActive && isCallFields && isPayload && animationDone && isAssistant;
  const isTodaySessionCard = isProgramFields && isPayload && animationDone && isAssistant;
  const displayChips = !isEmpty(contentRecommendations) && animationDone && isAssistant;

  useEffect(() => {
    if (!isNew) return;
    setDisplayedContent('');
    let index = 0;
    const interval = setInterval(() => {
      index += 2;
      if (index >= content.length) {
        setDisplayedContent(content);
        onAnimationComplete();
        clearInterval(interval);
      } else {
        setDisplayedContent(content.slice(0, index));
      }
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'instant' });
    }, 16);
    return () => clearInterval(interval);
  }, [isNew, content, onAnimationComplete]);

  const handleSelectContent = (recommendation) => {
    const { id, contentType, title } = recommendation;
    handleTrackEvent('Recommendation clicked', { type: contentType, id, title });
    selectContent(recommendation);
    changeAssistantStatus(contentType);
  };

  return (
    <>
      <li className={`messages__msg ${role}`}>
        {role === CHAT_ROLES.USER ? <span>{messageHour}</span> : null}
        <div className="messages__msg_container">
          {isAssistant ? <AssistantAvatar typeAvatar="bubble" /> : null}
          <div className={`messages__msg_content ${role}`}>
            {isAssistant ? formatMessage(displayedContent) : content}
            {isAssistant ? <span>{messageHour}</span> : null}
          </div>
        </div>
      </li>
      {displayChips &&
        contentRecommendations.map((recommendation) => (
          <li
            key={recommendation.id}
            className={`messages__msg recommendations ${recommendation.contentType}${isNew ? ' animate-in' : ''}`}
          >
            <section className="msg-content">
              <div className="msg-content__img">
                <img src={recommendation.thumbnailUrl || fakeImg} alt={recommendation.title} />
                <div className="msg-content__icon">
                  {RECOMMENDATION_ASSETS[recommendation.contentType].icon || <GrMicrophone size={20} />}
                </div>
              </div>
              <h3 className="msg-content__title">{recommendation.title || ''}</h3>
              <p className="msg-content__subtitle">{stripHtml(recommendation.description)}</p>
            </section>
            <section className="msg-link">
              <p className="msg-link__label">
                <strong>
                  {t(
                    RECOMMENDATION_ASSETS[recommendation.contentType].linkLabel ||
                      'evieApp.assistant.recommendations.fallback',
                  )}
                </strong>
              </p>
              <HiOutlineArrowNarrowRight size={32} onClick={() => handleSelectContent(recommendation)} />
            </section>
            {/* <UserFeedback copyToClipboard={() => onCopyToClipboard(content)} /> */}
          </li>
        ))}
      {isScheduleCallCard && (
        <li className="messages__msg recommendations">
          <ScheduleCallCard eventId={eventId} eventKey={eventKey} eventType={type} eventTrigger={trigger} assistant />
        </li>
      )}
      {isTodaySessionCard && (
        <li className="messages__msg recommendations">
          <ChatProgramCard eventKey={eventKey} eventType={type} eventTrigger={trigger} />
        </li>
      )}
    </>
  );
};

MsgBubble.defaultProps = {
  isNew: false,
  onAnimationComplete: () => {},
};

MsgBubble.propTypes = {
  isNew: PropTypes.bool,
  onAnimationComplete: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.oneOf(Object.values(CHAT_ROLES)),
    createdAt: PropTypes.string,
    content: PropTypes.string,
    chatAction: PropTypes.shape({}),
    contentRecommendations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        contentType: PropTypes.oneOf(['exercise', 'audio', 'short_video', 'reading', 'squeeze']),
        title: PropTypes.string,
        description: PropTypes.string,
        thumbnailUrl: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default MsgBubble;
